import React from 'react'

interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, info)
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return <>{this.props.fallback}</>

      return (
        <div
          style={{
            padding: '32px',
            fontFamily: "'JetBrains Mono', 'Courier New', monospace",
            backgroundColor: 'var(--terminal-bg)',
            border: '1px solid var(--accent-red, #ff4444)',
            borderRadius: '6px',
            margin: '24px auto',
            maxWidth: '640px',
          }}
        >
          <div
            style={{
              color: 'var(--accent-red, #ff4444)',
              fontSize: '0.9rem',
              marginBottom: '12px',
              fontWeight: 700,
            }}
          >
            [FATAL ERROR] Application crashed
          </div>
          <div
            style={{
              color: 'var(--text-secondary)',
              fontSize: '0.82rem',
              marginBottom: '16px',
            }}
          >
            ERROR: {this.state.error?.message ?? 'Unknown error'}
          </div>
          <div style={{ color: 'var(--text-muted, #555)', fontSize: '0.78rem' }}>
            Stack trace logged to console.
          </div>
          <div style={{ marginTop: '20px' }}>
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              style={{
                background: 'none',
                border: '1px solid var(--accent-green)',
                color: 'var(--accent-green)',
                fontFamily: 'inherit',
                fontSize: '0.82rem',
                padding: '6px 14px',
                borderRadius: '3px',
                cursor: 'pointer',
                marginRight: '8px',
              }}
            >
              [retry]
            </button>
            <a
              href="/"
              style={{
                color: 'var(--accent-cyan)',
                fontSize: '0.82rem',
                textDecoration: 'none',
              }}
            >
              [go home]
            </a>
          </div>
        </div>
      )
    }

    return <>{this.props.children}</>
  }
}
